import { useClipboard, useDisclosure, useHover } from "@mantine/hooks";
import { IconCopy, IconReload } from "@tabler/icons-react";
import type { JSX } from "react";
import ActionButton from "../ActionButton";
import { ItemView } from "../sharedItems";
import type { ItemRoleNullable, ItemType } from "../utils";

interface IThreadItem {
    id: number | null;
    type: ItemType;
    role: ItemRoleNullable; // Only set for MESSAGE
    content: string;
    toolCallId: string | null;
    toolName: string | null;
    toolArguments: string | null;
    reasoningSummary: string[];
    context: boolean;
    threadItemRetrievalContext: {
        id: number;
        query: string;
        context: string;
        hydeQuery: string;
        avgRelevance: number;
        documentNames: string[];
    } | null;
    threadItemReadContext: {
        id: number;
        documentId: number;
        documentName: string;
        startPart: number;
        endPart: number;
        totalParts: number;
    } | null;
    threadItemBrainstormContext: {
        id: number;
        what: string;
        count: number;
        candidates: string[];
        probabilities: number[];
        selectedIndices: number[];
        epsilon: number;
    } | null;
}

interface IProps {
    item: IThreadItem;
    reAskAi: (item: IThreadItem) => Promise<void>;
}

function ThreadItem({ item, reAskAi }: IProps): JSX.Element | null {
    const { hovered, ref } = useHover<HTMLDivElement>();
    const [opened, { open, close }] = useDisclosure(!item.context);
    const clipboard = useClipboard();

    const copy = () => {
        clipboard.copy(item.content);
    };

    const actionButtons = (
        <>
            {item.id != null && item.role === "USER" && (
                <ActionButton onClick={() => reAskAi(item)} label="Ask AI again" icon={<IconReload stroke={1} />} />
            )}
            <ActionButton onClick={copy} label="Copy content" icon={<IconCopy stroke={1} />} />
        </>
    );

    // Normalize to shared item shape for rendering components
    const sharedItem = {
        id: item.id,
        type: item.type,
        role: item.role,
        content: item.content,
        context: item.context,
        toolCallId: item.toolCallId,
        toolName: item.toolName,
        toolArguments: item.toolArguments,
        retrievalContext: item.threadItemRetrievalContext
            ? {
                  query: item.threadItemRetrievalContext.query,
                  context: item.threadItemRetrievalContext.context,
                  hydeQuery: item.threadItemRetrievalContext.hydeQuery,
                  chunks: [],
                  avgRelevance: item.threadItemRetrievalContext.avgRelevance,
                  documentNames: item.threadItemRetrievalContext.documentNames,
              }
            : null,
        readContext: item.threadItemReadContext
            ? {
                  documentId: item.threadItemReadContext.documentId,
                  documentName: item.threadItemReadContext.documentName,
                  startPart: item.threadItemReadContext.startPart,
                  endPart: item.threadItemReadContext.endPart,
                  totalParts: item.threadItemReadContext.totalParts,
              }
            : null,
        brainstormContext: item.threadItemBrainstormContext
            ? {
                  what: item.threadItemBrainstormContext.what,
                  count: item.threadItemBrainstormContext.count,
                  candidates: item.threadItemBrainstormContext.candidates,
                  probabilities: item.threadItemBrainstormContext.probabilities,
                  selectedIndices: item.threadItemBrainstormContext.selectedIndices,
                  epsilon: item.threadItemBrainstormContext.epsilon,
              }
            : null,
        reasoningSummary: item.reasoningSummary ?? [],
    } as const;

    const effectiveActionButtons = hovered ? actionButtons : null;

    return (
        <ItemView
            item={sharedItem}
            opened={opened}
            onOpen={open}
            onClose={close}
            actionButtons={effectiveActionButtons}
            showActions={hovered}
            containerRef={ref}
        />
    );
}

export default ThreadItem;
